import React, { useEffect, useState } from "react";
import { ServicesListBlock } from "../index";
import dynamic from "next/dynamic";
const ListSection = dynamic(() => import("./ListSection"));
const BannerCard = dynamic(() => import("@/Components/pages/Services/Banner/BannerCard"), { ssr: true });
const MobileCardSlider = dynamic(() => import("@/Components/pages/Services/Banner/MobileCardSlider"), { ssr: false });

type BannerCardData = {
  id: number;
  title: string;
  description: string;
  image: any;
  link: {
    href: string;
    label: string;
  };
};

interface ServicesListProps {
  data: ServicesListBlock;
  bannerCards?: BannerCardData[];
  isMobile?: boolean;
}

const ServicesList: React.FC<ServicesListProps> = ({ data, bannerCards, isMobile }) => {
  const { servicesList } = data;
  const baseUrl = `${process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL}`;
  const [isScrolled, setIsScrolled] = useState(false);

  const slugify = (text: string) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // remove special chars
      .replace(/\s+/g, "-"); // spaces → dash

  useEffect(() => {
    const handleScroll = () => {
      // Change background when scrolled more than 10px
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);

    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <div className="relative">
        {/* Sticky Banner Cards - positioned above container */}
        {bannerCards && bannerCards.length > 0 && (
          <div
            className={`sticky top-[85px] z-40 transition-colors duration-300 ${isScrolled
              ? "bg-white"
              : "bg-[linear-gradient(to_bottom,black_0%,black_50%,white_50%,white_100%)]"
              }`}
          >
            <div className="container relative z-30">
              {/* Desktop */}
              {!isMobile && (
                <div className="grid-cols-4 gap-[30px] xl:mt-[-60px] mt-[-55px] md:grid">
                  {bannerCards.map((card, index) => (
                    <BannerCard
                      key={card.id}
                      title={card.title}
                      theme={index % 2 === 0 ? "blue" : "green"} // blue, green, blue...
                      onclick={() => {
                        const target = document.getElementById(
                          card?.link?.href?.replace("#", "") || slugify(card.title),
                        );

                        if (target) {
                          const y =
                            target.getBoundingClientRect().top + window.scrollY - 280; // offset for header + cards
                          window.scrollTo({ top: y, behavior: "smooth" });
                        }
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Mobile Slider */}
              {isMobile && <MobileCardSlider data={{ bannerCard: bannerCards }} />}
            </div>
          </div>
        )}

        {servicesList?.map((service, index) => (
          <ListSection
            key={index}
            heading={service?.title}
            sections={service?.cards?.map((card) => ({
              readMore: card?.WhatWeOffer?.readMore,
              offerItems: card?.WhatWeOffer?.link?.map((link: any) => ({
                label: link?.label,
                link: link?.href,
              })),
              offerTitle: card.title || "",
              WhatWeOffer: card.WhatWeOffer.title || "",
              caseStudyText: card.featureCards[0]?.title || "",
              caseStudySubtitle: card.featureCards[0]?.sub_title || "",
              caseStudyImage: `${baseUrl}${card.featureCards[0]?.image?.data?.attributes?.url || ""}`,
              caseStudyButtonLink: card.featureCards[0]?.link[0]?.href,
              caseStudyButtonLabel: card.featureCards[0]?.link[0]?.label,
            }))}
          />
        ))}
      </div>
    </>
  );
};
export default ServicesList;